import { useCallback } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import useRefineQueryParams from './hooks/use-refine-query-params';

import Checkbox from '@klv/molecules/form-field/checkbox/checkbox';
import { getRegion, updateSelectQueryParam } from './utils';

import type { IRefinementListItem } from './types';
import type { UseRefinementListProps } from 'react-instantsearch';

const UnifiedSearchRefinementList = (
  props: UseRefinementListProps & { 
    backgroundColor?: 'dark' | 'light';
    label: string;
  },
) => {
  const { attribute, backgroundColor = 'light' } = props || {};

  const { items, refine } = useRefinementList(props);
  const { setIndexUiState } = useInstantSearch();

  /* 
   * setIndexUiState enables us to update a facet controlled by 
   * a different instance of useRefinementList.
  */
  const refineDependantFacets = useCallback((
    facetAttribute: string,
    value: string,
    isRefined: boolean,
  ) => {
    if (facetAttribute === 'types' && value === 'Live training') {
      const region = getRegion();

      if (isRefined || (!isRefined && region)) {        
        setIndexUiState((prevIndexUiState) => {
          const prevTypes = prevIndexUiState?.refinementList?.types || [];
          const prevRegion = prevIndexUiState?.refinementList?.region || [];

          const filteredTags = prevTypes.filter((type) => type !== value);

          const types = isRefined ? filteredTags : [...prevTypes, value];
          const regions = isRefined ? [] : (prevRegion.length > 0 ? prevRegion : [region]);

          updateSelectQueryParam([...(isRefined ? prevRegion : [region])]);

          return {
            ...prevIndexUiState,
            refinementList: {
              ...prevIndexUiState.refinementList,
              types: types,
              region: regions,
            },
          };
        });
        return true;
      }
    }
    return false;
  }, [setIndexUiState]);

  useRefineQueryParams({ items, refine });

  const selectFacetItem = (item: IRefinementListItem) => {
    const { isRefined, value } = item;

    updateSelectQueryParam(value);

    if (!refineDependantFacets(attribute, value, isRefined)) {
      // only refine current facet item
      refine(value);
    }
  };

  return (
    <div className="ais-RefinementList">
      <ul className="ais-RefinementList-list">
        {items.map((item) => {
          if (item.value !== '') {
            return (
              <li key={item.value} className="ais-RefinementList-item">
                <Checkbox
                  disabled='enabled'
                  className="ais-RefinementList-label"
                  size="medium"
                  id={`facet-${item.value}`}
                  backgroundColor={backgroundColor}
                  label={(
                    <>
                      <span title={item.label} className="ais-RefinementList-labelText">{item.label}</span>
                      <span className="ais-RefinementList-count">{item.count}</span>
                    </>
                  )}
                  name={item.value}
                  checked={item.isRefined}
                  onChange={() => selectFacetItem(item)}
                />
              </li>
            );
          }
          return null;
        })}
      </ul>
    </div>
  );
};

export default UnifiedSearchRefinementList;
